import _ from 'lodash';
import i18n from './i18n';

export const ALLOWED_FORMULA_FIELD_TYPES = ['text', 'date', 'number', 'progress', 'stars', 'switch', 'dropdown', 'checkboxes'];

export const ALLOWED_LOOKUP_FIELD_TYPES = [...ALLOWED_FORMULA_FIELD_TYPES, 'object', 'user'];

export const FORMULA_PROPERTIES = {
  CONFIG: 'formulaConfig',
};

const { t } = i18n;

export const FORMULA_TYPES = {
  USER: 'user',
  FORMULA: 'formula',
  ROLLUP: 'rollup',
  LOOKUP: 'lookup',
  AI: 'ai'
};

// Типы, значение которых вычисляется автоматически (пользователь не вводит вручную)
export const COMPUTED_FORMULA_TYPES = [FORMULA_TYPES.ROLLUP, FORMULA_TYPES.LOOKUP];

export const ROLLUP_FUNCTIONS = {
  SUM: 'sum',
  AVG: 'avg',
  MIN: 'min',
  MAX: 'max',
  COUNT: 'count',
  MODE: 'mode',
};

export const TEXT_FUNCTIONS = [
  'CONCAT',
  'JOIN',
  'LEFT',
  'LEN',
  'LOWER',
  'REPEAT',
  'REPLACE',
  'RIGHT',
  'SEARCH',
  'SUBSTRING',
  'TOSTRING',
  'TRIM',
  'UPPER',
  'REGEXMATCH',
  'REGEXREPLACE',
];

export const NUMBER_FUNCTIONS = [
  'ABS',
  'AVERAGE',
  'CEIL',
  'EXP',
  'FLOOR',
  'MAX',
  'MIN',
  'MOD',
  'POWER',
  'RANDOM',
  'ROUND',
  'SQRT',
  'SUM',
  'TONUMBER',
  'FIXED',
  'LN',
  'LOG',
  'LOG10',
  'SIN',
  'COS',
  'TAN',
  'COT',
  'ASIN',
  'ACOS',
  'ATAN',
  'ACOT',
  'PI',
];

export const DATE_FUNCTIONS = [
  'DATE',
  'DATEADD',
  'DATESUB',
  'DATEDIFF',
  'STARTOF',
  'ENDOF',
  'DATEFORMAT',
  'DATEPARSE',
  'SETYEAR',
  'SETQUARTER',
  'SETMONTH',
  'SETDAY',
  'SETHOUR',
  'SETMINUTE',
  'SETSECOND',
  'SETWEEKDAY',
  'SETTIME',
  'SETWEEKNUM',
  'YEAR',
  'QUARTER',
  'MONTH',
  'DAY',
  'HOUR',
  'MINUTE',
  'SECOND',
  'WEEKDAY',
  'WEEKNUM',
  'TIMESTAMP',
  // 'NOW',
  // 'TODAY',
];

export const BPIUM_FUNCTIONS = ['CATALOGID', 'RECORDID'];

export const LOGIC_FUNCTIONS = ['IF', 'ISEMPTY'];

export const ARRAY_FUNCTIONS = ['INDEX', 'ID', 'NAME', 'COUNT', 'UNIQUE', 'SLICE', 'FIND', 'FILTER', 'SORT'];

// сортируем по алфавиту функции
[TEXT_FUNCTIONS, NUMBER_FUNCTIONS, DATE_FUNCTIONS, LOGIC_FUNCTIONS, BPIUM_FUNCTIONS, ARRAY_FUNCTIONS].forEach((arr) => {
  arr.sort((a, b) => a.localeCompare(b));
});

const typesTrsPrefix = 'catalogEditor.formula.editor.types.';
const stringTranslate = t(`${typesTrsPrefix}string`);
const numberTranslate = t(`${typesTrsPrefix}number`);
const dateTranslate = t(`${typesTrsPrefix}date`);
const boolTranslate = t(`${typesTrsPrefix}boolean`);
const anyTranslate = t(`${typesTrsPrefix}any`);
const arrayTranslate = t(`${typesTrsPrefix}array`);

export const FORMULA_LANGUAGE = {
  FUNCTIONS: [
    ...TEXT_FUNCTIONS,
    ...NUMBER_FUNCTIONS,
    ...DATE_FUNCTIONS,
    ...LOGIC_FUNCTIONS,
    ...BPIUM_FUNCTIONS,
    ...ARRAY_FUNCTIONS,
  ],

  OPERATORS: ['+', '-', '/', '*', '%', '^', '|', '==', '!=', '>', '<', 'not', '&', '&&', '>=', '<=', '!'],
  KEYWORDS: ['true', 'false'],
};

const formulaCompletes = {
  // TEXT
  CONCAT: {
    label: 'CONCAT',
    detail: `function CONCAT(...values: (${stringTranslate} | ${numberTranslate})[]): ${stringTranslate}`,
    insertText: 'CONCAT',
  },
  TRIM: {
    label: 'TRIM',
    detail: `function TRIM(position: "leading" | "trailing" | "both", chars: ${stringTranslate}, str: ${stringTranslate}): ${stringTranslate}`,
    insertText: 'TRIM',
  },
  SEARCH: {
    label: 'SEARCH',
    detail: `function SEARCH(substring: ${stringTranslate}, text: ${stringTranslate}): ${numberTranslate}`,
    insertText: 'SEARCH',
  },
  REPLACE: {
    label: 'REPLACE',
    detail: `function REPLACE(text: ${stringTranslate}, search: ${stringTranslate}, replacement: ${stringTranslate}): ${stringTranslate}`,
    insertText: 'REPLACE',
  },
  LOWER: {
    label: 'LOWER',
    detail: `function LOWER(text: ${stringTranslate}): ${stringTranslate}`,
    insertText: 'LOWER',
  },
  UPPER: {
    label: 'UPPER',
    detail: `function UPPER(text: ${stringTranslate}): ${stringTranslate}`,
    insertText: 'UPPER',
  },
  REPEAT: {
    label: 'REPEAT',
    detail: `function REPEAT(text: ${stringTranslate}, times: ${numberTranslate}): ${stringTranslate}`,
    insertText: 'REPEAT',
  },
  SUBSTRING: {
    label: 'SUBSTRING',
    detail: `function SUBSTRING(text: ${stringTranslate}, start: ${numberTranslate}, length: ${numberTranslate}): ${stringTranslate}`,
    insertText: 'SUBSTRING',
  },
  LEFT: {
    label: 'LEFT',
    detail: `function LEFT(text: ${stringTranslate}, count: ${numberTranslate}): ${stringTranslate}`,
    insertText: 'LEFT',
  },
  RIGHT: {
    label: 'RIGHT',
    detail: `function RIGHT(text: ${stringTranslate}, count: ${numberTranslate}): ${stringTranslate}`,
    insertText: 'RIGHT',
  },
  LEN: {
    label: 'LEN',
    detail: `function LEN(text: ${stringTranslate}): ${numberTranslate}`,
    insertText: 'LEN',
  },
  JOIN: {
    label: 'JOIN',
    detail: `function JOIN(values: ${arrayTranslate}, separator: ${stringTranslate}, attr?: ${stringTranslate}): ${stringTranslate}`,
    insertText: 'JOIN',
  },
  TOSTRING: {
    label: 'TOSTRING',
    detail: `function TOSTRING(value: ${anyTranslate}): ${stringTranslate}`,
    insertText: 'TOSTRING',
  },
  REGEXMATCH: {
    label: 'REGEXMATCH',
    detail: `function REGEXMATCH(str: ${stringTranslate}, regex: ${stringTranslate}, mode?: ${numberTranslate}): ${boolTranslate}`,
    insertText: 'REGEXMATCH',
  },
  REGEXREPLACE: {
    label: 'REGEXREPLACE',
    detail: `function REGEXREPLACE(str: ${stringTranslate}, regex: ${stringTranslate}, replacement: ${stringTranslate}, mode?: ${numberTranslate}): ${boolTranslate}`,
    insertText: 'REGEXREPLACE',
  },

  // NUMBER
  ABS: {
    label: 'ABS',
    detail: `function ABS(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'ABS',
  },
  CEIL: {
    label: 'CEIL',
    detail: `function CEIL(value: ${numberTranslate}, decimals: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'CEIL',
  },
  FLOOR: {
    label: 'FLOOR',
    detail: `function FLOOR(value: ${numberTranslate}, decimals: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'FLOOR',
  },
  EXP: {
    label: 'EXP',
    detail: `function EXP(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'EXP',
  },
  MOD: {
    label: 'MOD',
    detail: `function MOD(a: ${numberTranslate}, b: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'MOD',
  },
  POWER: {
    label: 'POWER',
    detail: `function POWER(base: ${numberTranslate}, exponent: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'POWER',
  },
  ROUND: {
    label: 'ROUND',
    detail: `function ROUND(value: ${numberTranslate}, decimals: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'ROUND',
  },
  SQRT: {
    label: 'SQRT',
    detail: `function SQRT(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'SQRT',
  },
  RANDOM: { label: 'RANDOM', detail: `function RANDOM(): ${numberTranslate}`, insertText: 'RANDOM' },
  SUM: {
    label: 'SUM',
    detail: `function SUM(...values: ${numberTranslate}[]): ${numberTranslate}`,
    insertText: 'SUM',
  },
  AVERAGE: {
    label: 'AVERAGE',
    detail: `function AVERAGE(...values: ${numberTranslate}[]): ${numberTranslate}`,
    insertText: 'AVERAGE',
  },
  MIN: {
    label: 'MIN',
    detail: `function MIN(...values: ${numberTranslate}[]): ${numberTranslate}`,
    insertText: 'MIN',
  },
  MAX: {
    label: 'MAX',
    detail: `function MAX(...values: ${numberTranslate}[]): ${numberTranslate}`,
    insertText: 'MAX',
  },
  TONUMBER: {
    label: 'TONUMBER',
    detail: `function TONUMBER(value: ${anyTranslate}): ${numberTranslate}`,
    insertText: 'TONUMBER',
  },
  FIXED: {
    label: 'FIXED',
    detail: `function FIXED(value: ${numberTranslate}, decimals?: ${numberTranslate}): ${stringTranslate}`,
    insertText: 'FIXED',
  },
  LN: {
    label: 'LN',
    detail: `function LN(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'LN',
  },
  LOG: {
    label: 'LOG',
    detail: `function LOG(value: ${numberTranslate}, base?: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'LOG',
  },
  LOG10: {
    label: 'LOG10',
    detail: `function LOG10(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'LOG10',
  },
  SIN: {
    label: 'SIN',
    detail: `function SIN(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'SIN',
  },
  COS: {
    label: 'COS',
    detail: `function COS(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'COS',
  },
  TAN: {
    label: 'TAN',
    detail: `function TAN(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'TAN',
  },
  COT: {
    label: 'COT',
    detail: `function COT(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'COT',
  },
  ASIN: {
    label: 'ASIN',
    detail: `function ASIN(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'ASIN',
  },
  ACOS: {
    label: 'ACOS',
    detail: `function ACOS(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'ACOS',
  },
  ATAN: {
    label: 'ATAN',
    detail: `function ATAN(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'ATAN',
  },
  ACOT: {
    label: 'ACOT',
    detail: `function ACOT(value: ${numberTranslate}): ${numberTranslate}`,
    insertText: 'ACOT',
  },
  PI: {
    label: 'PI',
    detail: `function PI(): ${numberTranslate}`,
    insertText: 'PI',
  },

  // DATE
  DATE: {
    label: 'DATE',
    detail: `function DATE(year: ${numberTranslate}, month: ${numberTranslate}, day: ${numberTranslate}, hour?: ${numberTranslate}, minute?: ${numberTranslate}, second?: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'DATE',
  },
  DATEADD: {
    label: 'DATEADD',
    detail: `function DATEADD(date: ${dateTranslate}, amount: ${numberTranslate}, unit: ${stringTranslate}): ${dateTranslate}`,
    insertText: 'DATEADD',
  },
  DATESUB: {
    label: 'DATESUB',
    detail: `function DATESUB(date: ${dateTranslate}, interval: ${numberTranslate}, unit: ${stringTranslate}): ${dateTranslate}`,
    insertText: 'DATESUB',
  },
  DATEDIFF: {
    label: 'DATEDIFF',
    detail: `function DATEDIFF(date1: ${dateTranslate}, date2: ${dateTranslate}, unit: ${stringTranslate}): ${numberTranslate}`,
    insertText: 'DATEDIFF',
  },
  DATEFORMAT: {
    label: 'DATEFORMAT',
    detail: `function DATEFORMAT(date: ${dateTranslate}, format: ${stringTranslate}): ${stringTranslate}`,
    insertText: 'DATEFORMAT',
  },
  DATEPARSE: {
    label: 'DATEPARSE',
    detail: `function DATEPARSE(text: ${stringTranslate}, format: ${stringTranslate}): ${dateTranslate}`,
    insertText: 'DATEPARSE',
  },
  DAY: {
    label: 'DAY',
    detail: `function DAY(date: ${dateTranslate}): ${numberTranslate}`,
    insertText: 'DAY',
  },
  HOUR: {
    label: 'HOUR',
    detail: `function HOUR(date: ${dateTranslate}): ${numberTranslate}`,
    insertText: 'HOUR',
  },
  MINUTE: {
    label: 'MINUTE',
    detail: `function MINUTE(date: ${dateTranslate}): ${numberTranslate}`,
    insertText: 'MINUTE',
  },
  MONTH: {
    label: 'MONTH',
    detail: `function MONTH(date: ${dateTranslate}): ${numberTranslate}`,
    insertText: 'MONTH',
  },
  QUARTER: {
    label: 'QUARTER',
    detail: `function QUARTER(date: ${dateTranslate}): ${numberTranslate}`,
    insertText: 'QUARTER',
  },
  // NOW: { label: 'NOW', detail: 'function NOW(): ${dateTranslate}', insertText: 'NOW' },
  SECOND: {
    label: 'SECOND',
    detail: `function SECOND(date: ${dateTranslate}): ${numberTranslate}`,
    insertText: 'SECOND',
  },
  // TODAY: {
  //   label: 'TODAY',
  //   detail: `function TODAY(): ${dateTranslate}`,
  //   insertText: 'TODAY',
  // },
  WEEKDAY: {
    label: 'WEEKDAY',
    detail: `function WEEKDAY(date: ${dateTranslate}): ${numberTranslate}`,
    insertText: 'WEEKDAY',
  },
  WEEKNUM: {
    label: 'WEEKNUM',
    detail: `function WEEKNUM(date: ${dateTranslate}): ${numberTranslate}`,
    insertText: 'WEEKNUM',
  },
  YEAR: {
    label: 'YEAR',
    detail: `function YEAR(date: ${dateTranslate}): ${numberTranslate}`,
    insertText: 'YEAR',
  },
  STARTOF: {
    label: 'STARTOF',
    detail: `function STARTOF(date: ${dateTranslate}, unit: ${stringTranslate}): ${dateTranslate}`,
    insertText: 'STARTOF',
  },
  ENDOF: {
    label: 'ENDOF',
    detail: `function ENDOF(date: ${dateTranslate}, unit: ${stringTranslate}): ${dateTranslate}`,
    insertText: 'ENDOF',
  },
  SETYEAR: {
    label: 'SETYEAR',
    detail: `function SETYEAR(date: ${dateTranslate}, year: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'SETYEAR',
  },
  SETQUARTER: {
    label: 'SETQUARTER',
    detail: `function SETQUARTER(date: ${dateTranslate}, quarter: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'SETQUARTER',
  },
  SETMONTH: {
    label: 'SETMONTH',
    detail: `function SETMONTH(date: ${dateTranslate}, month: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'SETMONTH',
  },
  SETWEEKDAY: {
    label: 'SETWEEKDAY',
    detail: `function SETWEEKDAY(date: ${dateTranslate}, weekday: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'SETWEEKDAY',
  },
  SETWEEKNUM: {
    label: 'SETWEEKNUM',
    detail: `function SETWEEKNUM(date: ${dateTranslate}, weeknum: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'SETWEEKNUM',
  },
  SETDAY: {
    label: 'SETDAY',
    detail: `function SETDAY(date: ${dateTranslate}, day: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'SETDAY',
  },
  SETTIME: {
    label: 'SETTIME',
    detail: `function SETTIME(date: ${dateTranslate}, time: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'SETTIME',
  },
  SETHOUR: {
    label: 'SETHOUR',
    detail: `function SETHOUR(date: ${dateTranslate}, hour: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'SETHOUR',
  },
  SETMINUTE: {
    label: 'SETMINUTE',
    detail: `function SETMINUTE(date: ${dateTranslate}, minute: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'SETMINUTE',
  },
  SETSECOND: {
    label: 'SETSECOND',
    detail: `function SETSECOND(date: ${dateTranslate}, second: ${numberTranslate}): ${dateTranslate}`,
    insertText: 'SETSECOND',
  },
  TIMESTAMP: {
    label: 'TIMESTAMP',
    detail: `function TIMESTAMP(date: ${dateTranslate}): ${numberTranslate}`,
    insertText: 'TIMESTAMP',
  },

  // LOGIC
  IF: {
    label: 'IF',
    detail: `function IF(condition: ${boolTranslate}, valueIfTrue: ${anyTranslate}, valueIfFalse: ${anyTranslate}): ${anyTranslate}`,
    insertText: 'IF',
  },
  ISEMPTY: {
    label: 'ISEMPTY',
    detail: `function ISEMPTY(value: ${anyTranslate}): ${boolTranslate}`,
    insertText: 'ISEMPTY',
  },

  // BPIUM
  RECORDID: { label: 'RECORDID', detail: `function RECORDID(): ${stringTranslate}`, insertText: 'RECORDID' },
  CATALOGID: { label: 'CATALOGID', detail: `function CATALOGID(): ${stringTranslate}`, insertText: 'CATALOGID' },

  // ARRAY
  INDEX: {
    label: 'INDEX',
    detail: `function INDEX(arr: ${arrayTranslate}, index: ${numberTranslate}, attr?: ${stringTranslate}): ${anyTranslate}`,
    insertText: 'INDEX',
  },
  ID: {
    label: 'ID',
    detail: `function ID(arr: ${arrayTranslate}): ${arrayTranslate}`,
    insertText: 'ID',
  },
  NAME: {
    label: 'NAME',
    detail: `function NAME(arr: ${arrayTranslate}): ${arrayTranslate}`,
    insertText: 'NAME',
  },
  COUNT: {
    label: 'COUNT',
    detail: `function COUNT(arr: ${arrayTranslate}): ${numberTranslate}`,
    insertText: 'COUNT',
  },
  UNIQUE: {
    label: 'UNIQUE',
    detail: `function UNIQUE(arr: ${arrayTranslate}, attr?: ${stringTranslate}): ${arrayTranslate}`,
    insertText: 'UNIQUE',
  },
  SLICE: {
    label: 'SLICE',
    detail: `function SLICE(arr: ${arrayTranslate}, start: ${numberTranslate}, end?: ${numberTranslate}, attr?: ${numberTranslate}): ${arrayTranslate}`,
    insertText: 'SLICE',
  },
  FIND: {
    label: 'FIND',
    detail: `function FIND(arr: ${arrayTranslate}, item: ${anyTranslate}, attr?: ${numberTranslate}): ${anyTranslate}`,
    insertText: 'FIND',
  },
  FILTER: {
    label: 'FILTER',
    detail: `function FILTER(arr: ${arrayTranslate}, item: ${anyTranslate}, attr?: ${numberTranslate}): ${anyTranslate}`,
    insertText: 'FILTER',
  },
  SORT: {
    label: 'SORT',
    detail: `function SORT(arr: ${arrayTranslate}, mode?: ${numberTranslate}, attr?: ${numberTranslate}): ${anyTranslate}`,
    insertText: 'SORT',
  },
};

const formulaHints = {
  // TEXT
  CONCAT: {
    label: `CONCAT(...values: (${stringTranslate} | ${numberTranslate})[]): ${stringTranslate}`,
    parameters: [{ label: 'values' }],
  },
  TRIM: {
    label: `TRIM(position: "leading" | "trailing" | "both", chars: ${stringTranslate}, str: ${stringTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'position' }, { label: 'chars' }, { label: 'str' }],
  },
  SEARCH: {
    label: `SEARCH(substring: ${stringTranslate}, text: ${stringTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'substring' }, { label: 'text' }],
  },
  REPLACE: {
    label: `REPLACE(text: ${stringTranslate}, search: ${stringTranslate}, replacement: ${stringTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'text' }, { label: 'search' }, { label: 'replacement' }],
  },
  LOWER: {
    label: `LOWER(text: ${stringTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'text' }],
  },
  UPPER: {
    label: `UPPER(text: ${stringTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'text' }],
  },
  REPEAT: {
    label: `REPEAT(text: ${stringTranslate}, times: ${numberTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'text' }, { label: 'times' }],
  },
  SUBSTRING: {
    label: `SUBSTRING(text: ${stringTranslate}, start: ${numberTranslate}, length: ${numberTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'text' }, { label: 'start' }, { label: 'length' }],
  },
  LEFT: {
    label: `LEFT(text: ${stringTranslate}, count: ${numberTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'text' }, { label: 'count' }],
  },
  RIGHT: {
    label: `RIGHT(text: ${stringTranslate}, count: ${numberTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'text' }, { label: 'count' }],
  },
  LEN: {
    label: `LEN(text: ${stringTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'text' }],
  },
  JOIN: {
    label: `JOIN(values: ${arrayTranslate}, separator: ${stringTranslate}, attr?: ${stringTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'values' }, { label: 'separator' }, { label: 'attr', required: false }],
  },
  TOSTRING: {
    label: `TOSTRING(value: ${anyTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'value' }],
  },
  REGEXMATCH: {
    label: `REGEXMATCH(str: ${stringTranslate}, regex: ${stringTranslate}, mode?: ${numberTranslate}): ${boolTranslate}`,
    parameters: [{ label: 'str' }, { label: 'regex' }, { label: 'mode', required: false }],
  },
  REGEXREPLACE: {
    label: `REGEXREPLACE(str: ${stringTranslate}, regex: ${stringTranslate}, replacement: ${stringTranslate}, mode?: ${numberTranslate}): ${boolTranslate}`,
    parameters: [{ label: 'str' }, { label: 'regex' }, { label: 'replacement' }, { label: 'mode', required: false }],
  },

  // NUMBER
  ABS: {
    label: `ABS(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  CEIL: {
    label: `CEIL(value: ${numberTranslate}, decimals: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }, { label: 'decimals' }],
  },
  FLOOR: {
    label: `FLOOR(value: ${numberTranslate}, decimals: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }, { label: 'decimals' }],
  },
  EXP: {
    label: `EXP(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  MOD: {
    label: `MOD(a: ${numberTranslate}, b: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'a' }, { label: 'b' }],
  },
  POWER: {
    label: `POWER(base: ${numberTranslate}, exponent: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'base' }, { label: 'exponent' }],
  },
  ROUND: {
    label: `ROUND(value: ${numberTranslate}, decimals: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }, { label: 'decimals' }],
  },
  SQRT: {
    label: `SQRT(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  RANDOM: {
    label: `RANDOM(): ${numberTranslate}`,
    parameters: [],
  },
  SUM: {
    label: `SUM(...values: ${numberTranslate}[]): ${numberTranslate}`,
    parameters: [{ label: 'values' }],
  },
  AVERAGE: {
    label: `AVERAGE(...values: ${numberTranslate}[]): ${numberTranslate}`,
    parameters: [{ label: 'values' }],
  },
  MIN: {
    label: `MIN(...values: ${numberTranslate}[]): ${numberTranslate}`,
    parameters: [{ label: 'values' }],
  },
  MAX: {
    label: `MAX(...values: ${numberTranslate}[]): ${numberTranslate}`,
    parameters: [{ label: 'values' }],
  },
  TONUMBER: {
    label: `TONUMBER(value: ${anyTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  FIXED: {
    label: `FIXED(value: ${numberTranslate}, decimals?: ${numberTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'value' }, { label: 'value', required: false }],
  },
  LN: {
    label: `LN(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  LOG: {
    label: `LOG(value: ${numberTranslate}, base?: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }, { label: 'base' }],
  },
  LOG10: {
    label: `LOG10(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  SIN: {
    label: `SIN(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  COS: {
    label: `COS(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  TAN: {
    label: `TAN(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  COT: {
    label: `COT(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  ASIN: {
    label: `ASIN(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  ACOS: {
    label: `ACOS(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  ATAN: {
    label: `ATAN(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  ACOT: {
    label: `ACOT(value: ${numberTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'value' }],
  },
  PI: {
    label: `PI(): ${numberTranslate}`,
    parameters: [],
  },

  // DATE
  DATE: {
    label: `DATE(year: ${numberTranslate}, month: ${numberTranslate}, day: ${numberTranslate}, hour?: ${numberTranslate}, minute?: ${numberTranslate}, second?: ${numberTranslate}): ${dateTranslate}`,
    parameters: [
      { label: 'year' },
      { label: 'month' },
      { label: 'day' },
      { label: 'hour', required: false },
      { label: 'minute', required: false },
      { label: 'second', required: false },
    ],
  },
  DATEADD: {
    label: `DATEADD(date: ${dateTranslate}, amount: ${numberTranslate}, unit: ${stringTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'amount' }, { label: 'unit' }],
  },
  DATESUB: {
    label: `DATESUB(date: ${dateTranslate}, interval: ${numberTranslate}, unit: ${stringTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'interval' }, { label: 'unit' }],
  },
  DATEDIFF: {
    label: `DATEDIFF(date1: ${dateTranslate}, date2: ${dateTranslate}, unit: ${stringTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date1' }, { label: 'date2' }, { label: 'unit' }],
  },
  DATEFORMAT: {
    label: `DATEFORMAT(date: ${dateTranslate}, format: ${stringTranslate}): ${stringTranslate}`,
    parameters: [{ label: 'date' }, { label: 'format' }],
  },
  DATEPARSE: {
    label: `DATEPARSE(text: ${stringTranslate}, format: ${stringTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'text' }, { label: 'format' }],
  },
  DAY: {
    label: `DAY(date: ${dateTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date' }],
  },
  HOUR: {
    label: `HOUR(date: ${dateTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date' }],
  },
  MINUTE: {
    label: `MINUTE(date: ${dateTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date' }],
  },
  MONTH: {
    label: `MONTH(date: ${dateTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date' }],
  },
  QUARTER: {
    label: `QUARTER(date: ${dateTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date' }],
  },
  // NOW: {
  //   label: 'NOW(): ${dateTranslate}',
  //   parameters: [],
  // },
  SECOND: {
    label: `SECOND(date: ${dateTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date' }],
  },
  // TODAY: {
  //   label: `TODAY(): ${dateTranslate}`,
  //   parameters: [],
  // },
  WEEKDAY: {
    label: `WEEKDAY(date: ${dateTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date' }],
  },
  WEEKNUM: {
    label: `WEEKNUM(date: ${dateTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date' }],
  },
  YEAR: {
    label: `YEAR(date: ${dateTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date' }],
  },
  STARTOF: {
    label: `STARTOF(date: ${dateTranslate}, unit: ${stringTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'unit' }],
  },
  ENDOF: {
    label: `ENDOF(date: ${dateTranslate}, unit: ${stringTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'unit' }],
  },
  SETYEAR: {
    label: `SETYEAR(date: ${dateTranslate}, year: ${numberTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'year' }],
  },
  SETDAY: {
    label: `SETDAY(date: ${dateTranslate}, day: ${numberTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'day' }],
  },
  SETTIME: {
    label: `SETTIME(date: ${dateTranslate}, day: ${numberTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'time' }],
  },
  SETQUARTER: {
    label: `SETQUARTER(date: ${dateTranslate}, quarter: ${numberTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'quarter' }],
  },
  SETMONTH: {
    label: `SETMONTH(date: ${dateTranslate}, month: ${numberTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'month' }],
  },
  SETWEEKDAY: {
    label: `SETWEEKDAY(date: ${dateTranslate}, weekday: ${numberTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'weekday' }],
  },
  SETWEEKNUM: {
    label: `SETWEEKNUM(date: ${dateTranslate}, weeknum: ${numberTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'weeknum' }],
  },
  SETHOUR: {
    label: `SETHOUR(date: ${dateTranslate}, hour: ${numberTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'hour' }],
  },
  SETMINUTE: {
    label: `SETMINUTE(date: ${dateTranslate}, minute: ${numberTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'minute' }],
  },
  SETSECOND: {
    label: `SETSECOND(date: ${dateTranslate}, second: ${numberTranslate}): ${dateTranslate}`,
    parameters: [{ label: 'date' }, { label: 'second' }],
  },
  TIMESTAMP: {
    label: `TIMESTAMP(date: ${dateTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'date' }],
  },

  // LOGIC
  IF: {
    label: `IF(condition: ${boolTranslate}, valueIfTrue: ${anyTranslate}, valueIfFalse: ${anyTranslate}): ${anyTranslate}`,
    parameters: [{ label: 'condition' }, { label: 'valueIfTrue' }, { label: 'valueIfFalse' }],
  },
  ISEMPTY: {
    label: `ISEMPTY(value: ${anyTranslate}): ${boolTranslate}`,
    parameters: [{ label: 'value' }],
  },

  // BPIUM
  RECORDID: { label: `RECORDID(): ${stringTranslate}`, parameters: [] },
  CATALOGID: { label: `CATALOGID(): ${stringTranslate}`, parameters: [] },

  // ARRAY
  INDEX: {
    label: `INDEX(arr: ${arrayTranslate}, index: ${numberTranslate}, attr?: ${stringTranslate}): ${anyTranslate}`,
    parameters: [{ label: 'arr' }, { label: 'index' }, { label: 'attr', required: false }],
  },
  ID: {
    label: `ID(arr: ${arrayTranslate}): ${arrayTranslate}`,
    parameters: [{ label: 'arr' }],
  },
  NAME: {
    label: `NAME(arr: ${arrayTranslate}): ${arrayTranslate}`,
    parameters: [{ label: 'arr' }],
  },
  COUNT: {
    label: `COUNT(arr: ${arrayTranslate}): ${numberTranslate}`,
    parameters: [{ label: 'arr' }],
  },
  UNIQUE: {
    label: `UNIQUE(arr: ${arrayTranslate}, attr?: ${stringTranslate}): ${arrayTranslate}`,
    parameters: [{ label: 'arr' }, { label: 'attr', required: false }],
  },
  SLICE: {
    label: `SLICE(arr: ${arrayTranslate}, start: ${numberTranslate}, end?: ${numberTranslate}, attr?: ${numberTranslate}): ${arrayTranslate}`,
    parameters: [
      { label: 'arr' },
      { label: 'start' },
      { label: 'end', required: false },
      { label: 'attr', required: false },
    ],
  },
  FIND: {
    label: `FIND(arr: ${arrayTranslate}, item: ${anyTranslate}, attr?: ${numberTranslate}): ${anyTranslate}`,
    parameters: [{ label: 'arr' }, { label: 'item' }, { label: 'attr', required: false }],
  },
  FILTER: {
    label: `FILTER(arr: ${arrayTranslate}, item: ${anyTranslate}, attr?: ${numberTranslate}): ${anyTranslate}`,
    parameters: [{ label: 'arr' }, { label: 'item' }, { label: 'attr', required: false }],
  },
  SORT: {
    label: `SORT(arr: ${arrayTranslate}, mode?: ${numberTranslate}, attr?: ${numberTranslate}): ${anyTranslate}`,
    parameters: [{ label: 'arr' }, { label: 'mode', required: false }, { label: 'attr', required: false }],
  },
};

const trsPrefix = 'catalogEditor.formula.language';

export const FORMULA_LANGUAGE_COMPLETES = _.map(formulaCompletes, (complete) => ({
  ...complete,
  documentation: i18n.t(`${trsPrefix}.functions.${complete.label}.documentation`),
}));

export const FORMULA_LANGUAGE_HINTS = _.mapValues(formulaHints, ({ label, parameters }, key) => ({
  label,
  documentation: i18n.t(`${trsPrefix}.functions.${key}.documentation`),
  parameters: parameters.map(({ label: parameterLabel, required }, index) => ({
    label: parameterLabel,
    required,
    documentation: `${parameterLabel} — ${i18n.t(`${trsPrefix}.functions.${key}.parameters.${index}`)}`,
  })),
}));

/**
 * @typedef {'formula' | 'rollup' | 'lookup'} FormulaType
 */

/**
 * Общая структура поля с формулой, rollup или lookup логикой.
 *
 * @typedef {Object} FormulaField
 * @property {FormulaType} formulaType - Тип формульного поля:
 *   - `'formula'`: обычное математическое выражение,
 *   - `'rollup'`: агрегация по связанным данным,
 *   - `'lookup'`: прямой перенос значения из связанного поля.
 * @property {FormulaConfig} formulaConfig - Конфигурация для вычисления значения.
 */

/**
 * Конфигурация поля в зависимости от типа формулы.
 *
 * @typedef {FormulaConfigFormula | FormulaConfigRollup | FormulaConfigLookup} FormulaConfig
 */

/**
 * Конфигурация обычной формулы.
 *
 * @typedef {Object} FormulaConfigFormula
 * @property {string} formula - Формульное выражение, например `"1 + 2 * {{some}}"`.
 */

/**
 * Конфигурация rollup-поля (агрегации).
 *
 * @typedef {Object} FormulaConfigRollup
 * @property {string} linkCatalogId - ID каталога, с которым связано поле.
 * @property {string} linkFieldId - ID поля внутри связанного каталога, которое агрегируется.
 * @property {string} fn - Функция агрегации, например `'sum'`, `'avg'`, `'count'`, `'concat'` и др.
 * @property {Object} [fnArgs] - Дополнительные параметры для функции, например аргументы для `concat`.
 */

/**
 * Конфигурация lookup-поля (копирование значения).
 *
 * @typedef {Object} FormulaConfigLookup
 * @property {string} linkCatalogId - ID каталога, с которым связано поле.
 * @property {string} linkFieldId - ID поля, значение которого нужно скопировать.
 * @property {boolean} [sync=false] - Нужно ли синхронизировать значение при изменениях (по умолчанию `false`).
 */

export const FORMULA_REQUIRED_CONFIG_PARAMS = {
  formula: ['formula'],
  rollup: ['linkCatalogId', 'linkFieldId', 'fn'],
  lookup: ['linkCatalogId', 'linkFieldId'], // 'sync' не обязателен
};

export const FORMULA_VARIABLE_TEMPLATE = {
  prefix: '{',
  postfix: '}',

  trigger: '/',

  // Регулярка для подсветки переменных (например, {var1})
  get highlightRegexp() {
    return new RegExp(
      `${escapeRegExp(this.prefix)}([^${escapeRegExp(this.postfix)}]+)${escapeRegExp(this.postfix)}`,
      'g',
    );
  },

  // Регулярка для поиска переменных (например, {var1}, { var1 }, {var_1}, {Оценка звездами})
  get findVariablesRegexp() {
    return new RegExp(
      `${escapeRegExp(this.prefix)}\\s*([^${escapeRegExp(this.postfix)}]+?)\\s*${escapeRegExp(this.postfix)}`,
      'g',
    );
  },
};

// Утилита для экранирования символов в регулярных выражениях
function escapeRegExp(str) {
  return str.replace(/[.*+?^=!:${}()|\[\]\/\\]/g, '\\$&');
}
